import React from 'react';
import PageHero from './PageHero';
import { Heart, ShieldCheck, Stethoscope, Sparkles } from 'lucide-react';
import studentsImg from '../assets/Banner-GMIS Students.png';
import campusImg from '../assets/GMIS Backyard View.png';

export default function WellbeingSafeguardingPage({ setPage }) {
  return (
    <div className="w-full bg-[#f8f9fa] min-h-screen text-ink">
      {/* ── Hero ─────────────────────────────────────────────────────────────── */}
      <PageHero
        title="Well-being & Safeguarding"
        eyebrow="School Life at GMIS Jakarta"
        breadcrumbs={["Home", "School Life", "Well-being & Safeguarding"]}
        bgImage={studentsImg}
      />

      {/* ── Main Content Container ────────────────────────────────────────── */}
      <div className="max-w-[1200px] mx-auto px-6 lg:px-[80px] py-16 lg:py-24 box-border">

        {/* ── Section 1: Well-being ── */}
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16 mb-20 lg:mb-28">
          <div className="w-full lg:w-5/12">
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-gold/15 rounded-2xl -z-10" />
              <img
                src={studentsImg}
                alt="Well-being at GMIS"
                className="relative z-10 w-full h-[360px] md:h-[420px] rounded-2xl shadow-xl object-cover border-4 border-white"
              />
              <div className="absolute -bottom-4 -right-4 w-20 h-20 bg-navy/10 rounded-2xl -z-10" />
            </div>
          </div>

          <div className="w-full lg:w-7/12 flex flex-col gap-6 text-left">
            <div>
              <div className="inline-flex items-center gap-2 mb-3 px-3.5 py-1 rounded-full border border-gold/30 bg-gold/10">
                <span className="text-gold text-[11px] font-bold tracking-[0.2em] uppercase">School Life</span>
              </div>
              <h2 className="m-0 font-heading font-bold text-3xl md:text-4xl text-navy tracking-tight">
                Well-being
              </h2>
              <div className="w-16 h-1 bg-gold mt-4" />
            </div>

            <p className="text-[17px] md:text-[19px] font-medium text-navy leading-relaxed">
              At GMIS Jakarta, the well-being of every student is an important part of school life. We strive to create a safe, inclusive and nurturing environment where students feel supported, respected and confident to learn and grow.
            </p>

            <div className="p-6 rounded-2xl bg-white border border-line shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-gold/15 text-gold-ink flex items-center justify-center shrink-0 mt-0.5">
                  <Sparkles className="w-5 h-5 text-gold-ink" />
                </div>
                <p className="text-[16px] text-ink/80 leading-relaxed m-0">
                  Beyond the classroom, students have opportunities to take part in workshops and activities that support their personal development, well-being and confidence. These experiences encourage students to explore important topics, develop healthy relationships and build skills that support them as they grow.
                </p>
              </div>
            </div>

            <div className="p-6 rounded-2xl bg-white border border-line shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center shrink-0 mt-0.5">
                  <Stethoscope className="w-5 h-5 text-emerald-600" />
                </div>
                <p className="text-[16px] text-ink/80 leading-relaxed m-0">
                  Our school is committed to the health and well-being of all students and staff. Medical facilities are available on school, with a qualified doctor, a full-time nurse and medical support throughout the school day to provide prompt attention when students need care.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Divider ── */}
        <div className="w-full h-px bg-line/80 my-16 lg:my-20" />

        {/* ── Section 2: Safeguarding ── */}
        <div className="flex flex-col lg:flex-row-reverse items-center gap-10 lg:gap-16 mb-20 lg:mb-28">
          <div className="w-full lg:w-5/12">
            <div className="relative">
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-navy/10 rounded-2xl -z-10" />
              <img
                src={campusImg}
                alt="Safeguarding at GMIS"
                className="relative z-10 w-full h-[360px] md:h-[420px] rounded-2xl shadow-xl object-cover border-4 border-white"
              />
              <div className="absolute -top-4 -left-4 w-20 h-20 bg-gold/15 rounded-2xl -z-10" />
            </div>
          </div>

          <div className="w-full lg:w-7/12 flex flex-col gap-6 text-left">
            <div>
              <div className="inline-flex items-center gap-2 mb-3 px-3.5 py-1 rounded-full border border-gold/30 bg-gold/10">
                <span className="text-gold text-[11px] font-bold tracking-[0.2em] uppercase">Child Protection</span>
              </div>
              <h2 className="m-0 font-heading font-bold text-3xl md:text-4xl text-navy tracking-tight">
                Safeguarding
              </h2>
              <div className="w-16 h-1 bg-gold mt-4" />
            </div>

            <p className="text-[17px] md:text-[19px] font-medium text-navy leading-relaxed">
              GMIS is committed to providing a safe and secure environment for children, staff and visitors. We promote a school culture where children and adults feel confident sharing concerns about their own safety or the well-being of others. Our approach to safeguarding focuses on protecting children from harm and supporting their mental, physical and emotional well-being.
            </p>

            <div className="p-6 rounded-2xl bg-white border border-line shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-navy/10 text-navy flex items-center justify-center shrink-0 mt-0.5">
                  <ShieldCheck className="w-5 h-5 text-navy" />
                </div>
                <p className="text-[16px] text-ink/80 leading-relaxed m-0">
                  Student safety is supported by security measures across the school, including CCTV monitoring, controlled access through barrier gates and security guards on site. The school also has a dedicated team to support student well-being and respond to concerns involving bullying or inappropriate behaviour, helping students feel safe, heard and supported.
                </p>
              </div>
            </div>

            <div className="p-6 rounded-2xl bg-white border border-line shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-navy/10 text-navy flex items-center justify-center shrink-0 mt-0.5">
                  <Heart className="w-5 h-5 text-navy" />
                </div>
                <p className="text-[16px] text-ink/80 leading-relaxed m-0">
                  Safety is part of everyday school life at GMIS. Students are guided to make safe, responsible and respectful choices across the classroom, sports facilities, laboratories, swimming activities, school transportation and school trips. Clear procedures are in place to support students in the event of illness, accidents or emergencies, with appropriate medical care and communication with families.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Section 3: Concluding Motto ── */}
        <div className="w-full bg-navy rounded-3xl p-10 md:p-14 text-center text-white shadow-xl relative overflow-hidden">
          <div className="w-16 h-1 bg-gold mx-auto mb-6" />
          <h3 className="font-heading font-bold text-2xl sm:text-3xl md:text-4xl leading-snug max-w-3xl mx-auto tracking-tight">
            “A safe environment. A caring community. A place where every student can thrive.”
          </h3>
          <div className="mt-8 flex justify-center">
            <button
              onClick={() => {
                if (setPage) setPage('contact');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="px-8 py-3.5 rounded-lg bg-gold text-navy font-heading font-bold text-xs uppercase tracking-widest hover:bg-gold/90 transition-all shadow-md"
            >
              Contact Us
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
